import { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import { usePathname } from 'src/routes/hooks';
import { NAV } from '../config-layout';
import { useNavData } from './config-navigation';
import NavContent from './nav-content';

// ----------------------------------------------------------------------

const TRANSITION_MS = 400;

type Props = {
  openNav?: boolean;
  onCloseNav?: VoidFunction;
  onExpand?: (v: boolean) => void;
  onPin?: (v: boolean) => void;
};

export default function NavVertical({ onExpand, onPin }: Props) {
  const pathname = usePathname();
  const navData = useNavData();
  const [hovered, setHovered] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [contentExpanded, setContentExpanded] = useState(false);
  const collapseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevPinned = useRef(false);

  const visuallyExpanded = hovered || pinned || dropdownOpen;

  useEffect(() => {
    const wasUnpinned = prevPinned.current && !pinned;
    prevPinned.current = pinned;

    if (collapseTimer.current) clearTimeout(collapseTimer.current);

    if (visuallyExpanded) {
      setContentExpanded(true);
    } else if (wasUnpinned) {
      // Retarder le collapse du contenu uniquement au unpin pour éviter le jank pendant l'animation
      collapseTimer.current = setTimeout(() => setContentExpanded(false), TRANSITION_MS);
    } else {
      setContentExpanded(false);
    }

    return () => { if (collapseTimer.current) clearTimeout(collapseTimer.current); };
  }, [visuallyExpanded, pinned]);

  useEffect(() => {
    onExpand?.(visuallyExpanded);
  }, [visuallyExpanded]);

  const clipCollapsed = `inset(0 ${NAV.W_VERTICAL_EXPANDED - NAV.W_VERTICAL}px 0 0)`;
  const clipExpanded = 'inset(0 0 0 0)';

  return (
    <Box
      component="nav"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          height: '100%',
          width: NAV.W_VERTICAL_EXPANDED,
          clipPath: visuallyExpanded ? clipExpanded : clipCollapsed,
          transition: 'clip-path 0.4s cubic-bezier(0.22, 1, 0.36, 1)',
          willChange: 'clip-path',
          zIndex: (theme) => theme.zIndex.appBar + 2,
        }}
      >
        <NavContent
          expanded={contentExpanded}
          navData={navData}
          pinned={pinned}
          onDropdownChange={setDropdownOpen}
          onTogglePin={() => {
            const next = !pinned;
            setPinned(next);
            onPin?.(next);
          }}
        />
      </Box>
    </Box>
  );
}
